import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ProductCategory {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCategoryFormData {
  name: string;
  sort_order?: number;
}

const QUERY_KEY = ['product-categories'];

export function useProductCategories() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all categories
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ProductCategory[]> => {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as ProductCategory[];
    },
  });

  // Create category
  const createMutation = useMutation({
    mutationFn: async (input: ProductCategoryFormData) => {
      // Get max sort_order to auto-assign next value
      const { data: existing } = await supabase
        .from('product_categories')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      const maxSortOrder = existing?.[0]?.sort_order ?? -1;

      const { data, error } = await supabase
        .from('product_categories')
        .insert({
          name: input.name,
          sort_order: input.sort_order ?? maxSortOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría creada',
        description: 'La categoría se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo crear la categoría: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update category
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: ProductCategoryFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('product_categories')
        .update({
          name: input.name,
          sort_order: input.sort_order,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría actualizada',
        description: 'La categoría se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar la categoría: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete category
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('product_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría eliminada',
        description: 'La categoría se ha eliminado correctamente.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la categoría. Verifica que no tenga productos asociados.',
        variant: 'destructive',
      });
    },
  });

  // Reorder categories with optimistic update
  const reorderMutation = useMutation({
    mutationFn: async ({
      categoryId,
      newSortOrder,
      swapCategoryId,
      swapSortOrder,
    }: {
      categoryId: string;
      newSortOrder: number;
      swapCategoryId: string;
      swapSortOrder: number;
    }) => {
      const [result1, result2] = await Promise.all([
        supabase
          .from('product_categories')
          .update({ sort_order: newSortOrder })
          .eq('id', categoryId),
        supabase
          .from('product_categories')
          .update({ sort_order: swapSortOrder })
          .eq('id', swapCategoryId),
      ]);

      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previousCategories = queryClient.getQueryData<ProductCategory[]>(QUERY_KEY);

      queryClient.setQueryData<ProductCategory[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        
        const updated = [...old];
        const idx1 = updated.findIndex(c => c.id === variables.categoryId);
        const idx2 = updated.findIndex(c => c.id === variables.swapCategoryId);
        
        if (idx1 !== -1 && idx2 !== -1) {
          [updated[idx1], updated[idx2]] = [updated[idx2], updated[idx1]];
          updated[idx1] = { ...updated[idx1], sort_order: variables.swapSortOrder };
          updated[idx2] = { ...updated[idx2], sort_order: variables.newSortOrder };
        }
        
        return updated;
      });

      return { previousCategories };
    },
    onError: (err, variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(QUERY_KEY, context.previousCategories);
      }
      toast({
        title: 'Error',
        description: 'No se pudo reordenar la categoría',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY, refetchType: 'all' });
    },
  });

  return {
    categories: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createCategory: createMutation.mutateAsync,
    updateCategory: updateMutation.mutateAsync,
    deleteCategory: deleteMutation.mutateAsync,
    reorderCategory: reorderMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
  };
}
